import { useState, useLayoutEffect, type RefObject, useRef } from 'react';

type ElementSize = {
  width: number;
  height: number;
};

function useElementSize(ref: RefObject<HTMLElement | null>): ElementSize {
  const [elementSize, setElementSize] = useState<ElementSize>(() => {
    const { width, height } = ref.current
      ? ref.current.getBoundingClientRect()
      : { width: 0, height: 0 };

    return { width, height };
  });
  const rafRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const maybeUpdateElementSize = ({ width, height }: DOMRect) => {
      if (rafRef.current) {
        return;
      }

      rafRef.current = requestAnimationFrame(() => {
        setElementSize((prev) => {
          if (prev.height !== height || prev.width !== width) {
            return {
              height,
              width,
            };
          }

          return prev;
        });

        rafRef.current = null;
      });
    };

    if (typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver((entries) => {
        if (entries.length === 0) {
          return;
        }

        maybeUpdateElementSize(entries[0].target.getBoundingClientRect());
      });

      resizeObserver.observe(ref.current);

      return () => {
        resizeObserver.disconnect();

        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      };
    }

    const handleResize = () => {
      if (!ref.current) {
        return;
      }

      maybeUpdateElementSize(ref.current.getBoundingClientRect());
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [ref]);

  return elementSize;
}

/**
 * @param ref
 * @param threshold
 * @param treatZeroAsTrue The size will be zero for the first frame / initial render, so this lets you determine whether that should be treated as above or below the threshold.
 */
export function useElementWidthIsAbove(
  ref: RefObject<HTMLElement | null>,
  threshold: number,
  treatZeroAsTrue = false,
): boolean {
  const { width } = useElementSize(ref);

  return (width === 0 && treatZeroAsTrue) || width > threshold;
}

/**
 * @param ref
 * @param threshold
 * @param treatZeroAsTrue The size will be zero for the first frame / initial render, so this lets you determine whether that should be treated as above or below the threshold.
 */
export function useElementHeightIsAbove(
  ref: RefObject<HTMLElement | null>,
  threshold: number,
  treatZeroAsTrue = false,
): boolean {
  const { height } = useElementSize(ref);

  return (height === 0 && treatZeroAsTrue) || height > threshold;
}

export default useElementSize;
